import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed

  const checkSession = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // data.status = "mfa_required" | "mfa_setup_required"
    return data;
  };

  const mfaSetup = async () => {
    const { data } = await axios.post(`${API}/auth/mfa/setup`, {}, { withCredentials: true });
    return data; // { secret, otpauth_url, qr_code }
  };

  const mfaEnable = async (code) => {
    const { data } = await axios.post(
      `${API}/auth/mfa/enable`,
      { code },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const mfaVerify = async (code) => {
    const { data } = await axios.post(
      `${API}/auth/mfa/verify`,
      { code },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const setNewPassword = async (newPassword, confirmPassword) => {
    const { data } = await axios.post(
      `${API}/auth/set-password`,
      { new_password: newPassword, confirm_password: confirmPassword },
      { withCredentials: true }
    );
    return data; // { status: "mfa_required" | "mfa_setup_required" }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, mfaSetup, mfaEnable, mfaVerify, setNewPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
